import type { ScheduleAuditAction, ScheduleAuditChanges, ScheduleAuditLog } from "../../../../types";

/* ----------------------------- action metadata ---------------------------- */

export interface AuditActionMeta {
    label: string;
    /** tailwind classes for the pill */
    pill: string;
    /** tailwind class for the timeline dot */
    dot: string;
}

const TONES = {
    green: { pill: "bg-green-50 text-green-700", dot: "bg-green-500" },
    blue: { pill: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
    red: { pill: "bg-red-50 text-red-700", dot: "bg-red-500" },
    purple: { pill: "bg-purple-50 text-purple-700", dot: "bg-purple-500" },
    amber: { pill: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
    gray: { pill: "bg-gray-100 text-gray-700", dot: "bg-gray-400" },
};

const ACTION_META: Record<ScheduleAuditAction, AuditActionMeta> = {
    create: { label: "Shift created", ...TONES.green },
    copy: { label: "Shift copied", ...TONES.green },
    update: { label: "Shift updated", ...TONES.blue },
    delete: { label: "Shift deleted", ...TONES.red },
    mass_delete: { label: "Shifts deleted", ...TONES.red },
    publish: { label: "Shift published", ...TONES.purple },
    publish_request: { label: "Publish requested", ...TONES.purple },
    open_shift_assign: { label: "Open shift assigned", ...TONES.amber },
    open_shift_release: { label: "Open shift released", ...TONES.amber },
    open_shift_status: { label: "Open shift status", ...TONES.amber },
    swap_request: { label: "Swap requested", ...TONES.amber },
    swap_complete: { label: "Swap completed", ...TONES.amber },
    swap_status: { label: "Swap status", ...TONES.amber },
};

export const AUDIT_ACTION_OPTIONS: Array<{ value: ScheduleAuditAction; label: string }> = (
    Object.keys(ACTION_META) as ScheduleAuditAction[]
).map((value) => ({ value, label: ACTION_META[value].label }));

export const getActionMeta = (action: string, label?: string): AuditActionMeta => {
    const known = ACTION_META[String(action).toLowerCase() as ScheduleAuditAction];
    const fallback = { label: toReadableLabel(action || "audit"), ...TONES.gray };
    const base = known || fallback;
    return label ? { ...base, label } : base;
};

export const SOURCE_LABELS: Record<string, string> = { web: "Web", api: "API", cron: "Cron" };

/* ------------------------------ value helpers ----------------------------- */

const FIELD_LABELS: Record<string, string> = {
    logs: "Notes",
    title: "Shift",
    shift: "Shift",
    shift_color: "Shift color",
    shift_tags_json: "Shift tags",
    department_code: "Department",
    os_shift_code: "Shift code",
    os_emp_start_datetime: "Start time",
    os_emp_end_datetime: "End time",
    os_work_codes_id: "Work code",
    us_user_site_profile_id: "Site",
    start: "Start",
    end: "End",
};

export const toReadableLabel = (value: string): string =>
    value
        .replace(/_json$/i, "")
        .replace(/^os_emp_/, "")
        .replace(/^os_/, "")
        .replace(/^us_/, "")
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

export const getFieldLabel = (field: string): string => FIELD_LABELS[field] || toReadableLabel(field);

export const parseJsonLike = (value: unknown): unknown => {
    if (typeof value !== "string") return value;
    let parsed: unknown = value.trim();
    for (let i = 0; i < 3; i += 1) {
        if (typeof parsed !== "string") break;
        const trimmed = parsed.trim();
        if (!trimmed) return "";
        if (!["{", "[", '"'].includes(trimmed.charAt(0))) return trimmed;
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return trimmed;
        }
    }
    return parsed;
};

export const formatDisplayValue = (value: unknown): string => {
    const parsed = parseJsonLike(value);
    if (parsed === null || parsed === undefined || parsed === "") return "-";
    if (Array.isArray(parsed)) {
        if (parsed.length === 0) return "-";
        return parsed.map((item) => formatDisplayValue(item)).join(", ");
    }
    if (typeof parsed === "object") {
        const entries = Object.entries(parsed as Record<string, unknown>).filter(
            ([, v]) => v !== null && v !== undefined && v !== ""
        );
        if (entries.length === 0) return "-";
        return entries.map(([k, v]) => `${getFieldLabel(k)}: ${formatDisplayValue(v)}`).join(", ");
    }
    if (typeof parsed === "boolean") return parsed ? "Yes" : "No";
    return String(parsed);
};

export const isColorField = (field: string): boolean => field.toLowerCase().includes("color");
export const isHexColor = (value: string): boolean => /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value);

/** `changes` (new) with fallback to `changes_json` (legacy), always as an object. */
export const getChanges = (log: ScheduleAuditLog): ScheduleAuditChanges => {
    const raw = parseJsonLike(log.changes ?? log.changes_json ?? null);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return raw as ScheduleAuditChanges;
};

export interface ChangeRow {
    field: string;
    label: string;
    old?: unknown;
    new?: unknown;
    /** true when this is a {old,new} diff, false for a plain snapshot value */
    isDiff: boolean;
}

const START_FIELD = "os_emp_start_datetime";
const END_FIELD = "os_emp_end_datetime";
const STATUS_FIELDS = ["publish", "was_published", "is_published"];

/** Id fields the API resolves to names (`labels`, `old_label`/`new_label`, `<field>_label`). */
const LABELED_FIELDS: Record<string, string> = {
    us_user_site_profile_id: "Site",
    os_work_codes_id: "Work code",
    department_code: "Department",
    os_emp_account_id: "Employee",
};
const isInternalIdField = (field: string): boolean => /(^|_)ids?$/i.test(field);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseLocal = (value: unknown): Date | null => {
    if (typeof value !== "string") return null;
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0));
};

const timeText = (d: Date): string => {
    const h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${h % 12 || 12}:${min} ${h < 12 ? "AM" : "PM"}`;
};
const dayText = (d: Date): string =>
    `${MONTHS[d.getMonth()]} ${d.getDate()}${d.getFullYear() !== new Date().getFullYear() ? `, ${d.getFullYear()}` : ""}`;
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** "Nov 3, 7:00 AM" */
const formatDateTimeValue = (value: unknown): string => {
    const d = parseLocal(value);
    return d ? `${dayText(d)}, ${timeText(d)}` : formatDisplayValue(value);
};

/** "Nov 3, 7:00 AM – 1:30 PM" (end date shown only when it differs) */
const formatRange = (start: unknown, end: unknown): string => {
    const s = parseLocal(start);
    const e = parseLocal(end);
    if (!s || !e) return `${formatDateTimeValue(start)} – ${formatDateTimeValue(end)}`;
    return sameDay(s, e)
        ? `${dayText(s)}, ${timeText(s)} – ${timeText(e)}`
        : `${dayText(s)}, ${timeText(s)} – ${dayText(e)}, ${timeText(e)}`;
};

const formatStatus = (value: unknown): string => {
    const v = parseJsonLike(value);
    if (v === true || v === 1 || v === "1") return "Published";
    if (v === false || v === 0 || v === "0") return "Draft";
    return formatDisplayValue(v);
};

export const getChangeRows = (log: ScheduleAuditLog): ChangeRow[] => {
    const changes = getChanges(log);
    const raw: ChangeRow[] = [];
    const labels = (parseJsonLike(log.labels) || {}) as Record<string, { old?: unknown; new?: unknown }>;
    const nameOf = (field: string, side: "old" | "new", inline: unknown, raw: unknown): unknown => {
        if (!(field in LABELED_FIELDS)) return raw;
        const label = inline ?? labels[field]?.[side];
        if (label !== undefined && label !== null && label !== "") return label;
        // deleted / unresolved: keep a readable code for department, otherwise don't expose the raw id
        return field === "department_code" ? raw : raw === undefined || raw === null || raw === "" ? raw : "Unknown";
    };

    Object.entries(changes).forEach(([field, value]) => {
        if (field.endsWith("_label") && (field.slice(0, -6) in changes)) return; // snapshot label, merged below
        const parsed = parseJsonLike(value);
        const isDiff =
            !!parsed && typeof parsed === "object" && !Array.isArray(parsed) && ("old" in (parsed as object) || "new" in (parsed as object));
        if (isDiff) {
            const v = parsed as { old?: unknown; new?: unknown; old_label?: unknown; new_label?: unknown };
            raw.push({
                field,
                label: LABELED_FIELDS[field] || getFieldLabel(field),
                old: nameOf(field, "old", v.old_label, v.old),
                new: nameOf(field, "new", v.new_label, v.new),
                isDiff: true,
            });
        } else {
            if (parsed === null || parsed === undefined || parsed === "") return;
            raw.push({
                field,
                label: LABELED_FIELDS[field] || getFieldLabel(field),
                new: nameOf(field, "new", (changes as Record<string, unknown>)[`${field}_label`], parsed),
                isDiff: false,
            });
        }
    });

    const byField = new Map(raw.map((r) => [r.field, r]));
    const start = byField.get(START_FIELD);
    const end = byField.get(END_FIELD);
    const rows: ChangeRow[] = [];

    raw.forEach((row) => {
        if (row.field === START_FIELD || row.field === END_FIELD) {
            if (start && end) {
                // merge start + end into one "Shift time" row, emitted at the start field's position
                if (row.field === START_FIELD) {
                    rows.push({
                        field: "shift_time",
                        label: "Shift time",
                        isDiff: start.isDiff || end.isDiff,
                        old: start.isDiff || end.isDiff ? formatRange(start.old ?? end.old, end.old ?? start.old) : undefined,
                        new: formatRange(start.new, end.new),
                    });
                }
            } else {
                rows.push({
                    ...row,
                    label: row.field === START_FIELD ? "Start time" : "End time",
                    old: row.isDiff ? formatDateTimeValue(row.old) : undefined,
                    new: formatDateTimeValue(row.new),
                });
            }
            return;
        }
        if (STATUS_FIELDS.includes(row.field)) {
            rows.push({ ...row, label: "Status", old: row.isDiff ? formatStatus(row.old) : undefined, new: formatStatus(row.new) });
            return;
        }
        if (row.field === "os_shift_code") return; // shift name is already shown
        if (isInternalIdField(row.field) && !(row.field in LABELED_FIELDS)) return;
        rows.push(row);
    });
    return rows;
};

/* ------------------------------- time helpers ----------------------------- */

/** `created` is UTC "YYYY-MM-DD HH:mm:ss" */
export const parseUtc = (value: string): Date | null => {
    if (!value) return null;
    const parsed = new Date(`${value.replace(" ", "T")}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const safeFormatter = (options: Intl.DateTimeFormatOptions, timeZone?: string) => {
    try {
        return new Intl.DateTimeFormat("en-US", { ...options, ...(timeZone ? { timeZone } : {}) });
    } catch {
        return new Intl.DateTimeFormat("en-US", options);
    }
};

export const formatTime = (created: string, timeZone?: string): string => {
    const d = parseUtc(created);
    return d ? safeFormatter({ hour: "numeric", minute: "2-digit" }, timeZone).format(d) : created;
};

export const formatFullDateTime = (created: string, timeZone?: string): string => {
    const d = parseUtc(created);
    return d
        ? safeFormatter(
              { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" },
              timeZone
          ).format(d)
        : created;
};

/** yyyy-MM-dd in the account timezone */
export const getDayKey = (created: string, timeZone?: string): string => {
    const d = parseUtc(created);
    if (!d) return created.slice(0, 10);
    const parts = safeFormatter({ year: "numeric", month: "2-digit", day: "2-digit" }, timeZone).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
};

export const formatDayHeading = (dayKey: string): string => {
    const d = new Date(`${dayKey}T12:00:00`);
    return Number.isNaN(d.getTime())
        ? dayKey
        : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

/** "yyyy-MM-dd" or "yyyy-MM-dd HH:mm:ss" (account local) -> "Oct 13, 9:00 AM" */
export const formatShiftDateTime = (value?: string | null): string => {
    if (!value) return "";
    const d = new Date(value.includes(" ") ? value.replace(" ", "T") : `${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return value.includes(" ")
        ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const isPublished = (value: ScheduleAuditLog["was_published"]): boolean | null => {
    if (value === true || value === 1 || value === "1") return true;
    if (value === false || value === 0 || value === "0") return false;
    return null;
};
